import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  generatePath, RouteComponentProps, useHistory, withRouter,
} from 'react-router-dom';
import { isFetching, ResourcesItem } from 'redux-and-the-rest';

import { GameScoreModel } from '../../../models/gameScoreModel';
import { RewardModel } from '../../../models/rewardModel';
import { chooseGameScoreReward, getOrFetchGameScores } from '../../../redux/reducers/gameScore';
import { ApplicationState } from '../../../redux/types';
import { Routes } from '../../../routes';
import { listStateHandler } from '../../../tools/reactUtils/stateUtils';
import LoadingIndicator from '../../shared/LoadingIndicator';
import ClaimableReward from './ClaimableReward';

import './Rewards.scss';

interface Props {
  islandId: string;
  unclaimedGameScore?: ResourcesItem<GameScoreModel>;
  fetchingData: boolean;
  onChoiceMade: (reward: RewardModel) => void;
  onResumePlay: () => void;
}

interface IClaimableReward {
  reward: RewardModel;
  claimed: boolean;
}

const WAIT_TIME_BEFORE_CHOOSING_REWARD = 3000;

const RewardClaim = ({
  islandId, unclaimedGameScore, fetchingData, onChoiceMade, onResumePlay,
}: Props): React.ReactElement => {
  const history = useHistory();

  /**
   * This component is slightly unconventional as the RATR state changes are dispatched after a short
   * period of time which allows us to create an unraveling effect of the hidden reward that they have
   * chose.
   * With that requirement in mind, we need a local state which tells which reward has been claimed
   * so that we can change their styling accordingly.
   */
  const [claimableRewards, setClaimableRewards] = listStateHandler(
    useState<IClaimableReward[]>([]),
  );

  useEffect(() => {
    /**
     * Set the rewards that the user can choose from.
     */
    if (unclaimedGameScore) {
      const { values: { rewardsToChooseFrom } } = unclaimedGameScore;

      if (rewardsToChooseFrom) {
        setClaimableRewards.setItems([...rewardsToChooseFrom.map((reward) => ({ claimed: false, reward }))]);
      }
    }
  }, [unclaimedGameScore]);

  /**
   * There is a brief period of time, after making the choice reward, in which we want to stop the
   * user from clicking and claiming the other rewards.
   */
  const canClaimRewards = !claimableRewards.find(({ claimed }) => (claimed));

  if (fetchingData) {
    return (
      <div className="loadable">
        <LoadingIndicator />
      </div>
    );
  }

  if (!unclaimedGameScore) {
    return (
      <div className="play-again">
        <button
          type="button"
          onClick={onResumePlay}
          className="button primary"
        >
          Keep Playing
        </button>

        <button
          type="button"
          onClick={() => history.push(generatePath(Routes.Islands.View, { id: islandId }))}
          className="button primary"
        >
          To the Island
        </button>
      </div>
    );
  }

  const { values: { score, total: maxScore } } = unclaimedGameScore;

  return (
    <>
      <div className="list claimable-reward-list">
        {claimableRewards.map(({ reward, claimed }, index) => (
          <ClaimableReward
            // eslint-disable-next-line react/no-array-index-key
            key={`${reward.id}-${index}`}
            reward={reward}
            claimed={claimed}
            canClaimRewards={canClaimRewards}
            onClick={() => {
              if (canClaimRewards) {
                setClaimableRewards.updateAtIndex(index, (claimableReward) => ({
                  ...claimableReward,
                  claimed: true,
                }));

                /**
                 * If we trigger the choosing of the reward straight away, the page will quickly change.
                 * We then trigger it after a short period of time, so that we can have some effects
                 * happening on the page.
                 */
                setTimeout(() => {
                  onChoiceMade(reward);
                }, WAIT_TIME_BEFORE_CHOOSING_REWARD);
              }
            }}
          />
        ))}
      </div>

      <div className="score-message">
        {`You scored ${score} / ${maxScore}`}
      </div>
    </>
  );
};

interface URLParams {
  islandId: string;
}

const mapStateToProps = (
  { gameScores }: ApplicationState,
  { match: { params: { islandId } } }: RouteComponentProps<URLParams>,

) => {
  const allGameScores = getOrFetchGameScores(gameScores, {
    include: 'game,chosenReward,rewardsToChooseFrom',
    islandId,
  });
  const fetchingGameScores = isFetching(allGameScores);
  const unclaimedGameScore = allGameScores.items.find(({ values: { chosenReward } }) => (!chosenReward));

  return {
    fetchingData: fetchingGameScores,
    unclaimedGameScore,
    islandId,
    onChoiceMade: (reward: RewardModel) => {
      if (unclaimedGameScore) {
        const { values } = unclaimedGameScore;

        chooseGameScoreReward({
          islandId,
          id: values.id,
          include: 'chosenReward',
        }, {
          ...unclaimedGameScore.values,
          chosenReward: reward,
          relationshipNames: ['chosenReward'],
        });
      }
    },
  };
};

export default withRouter(connect(mapStateToProps)(RewardClaim));
